import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Store,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  ChevronDown,
  Edit,
  ChevronLeft,
  ChevronRight,
  Package,
  Heart,
  Wallet,
  MessageCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { customToast } from '../../utils/customToast';
import { ProfileModal } from '../ProfileModal';
import { SubscriptionBadge } from '../SubscriptionBadge';

// Utility function to truncate text with ellipsis
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [sidebarProfileDropdownOpen, setSidebarProfileDropdownOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [logoImageFailed, setLogoImageFailed] = useState(false);

  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { getSetting } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  // Save collapsed state to localStorage
  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  // Mobile viewport height fix
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Helper function to get profile picture URL
  const getProfilePictureUrl = (user: any) => {
    if (user?.profile_picture_url) {
      return user.profile_picture_url;
    }
    if (user?.profile_picture) {
      if (user.profile_picture.startsWith('http')) {
        return user.profile_picture;
      }
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const storageBase = apiBase.replace('/api', '/storage');
      return `${storageBase}/${user.profile_picture}`;
    }
    return null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById('profile-dropdown');
      const sidebarDropdown = document.getElementById('sidebar-profile-dropdown');

      if (dropdown && !dropdown.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
      if (sidebarDropdown && !sidebarDropdown.contains(event.target as Node)) {
        setSidebarProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen || sidebarProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [profileDropdownOpen, sidebarProfileDropdownOpen]);

  // Map route paths to module names
  const getModuleForPath = (path: string): string | null => {
    const moduleMap: Record<string, string> = {
      '/admin': 'dashboard',
      '/admin/support-tickets': 'support_tickets',
      '/admin/shops': 'shops_management',
      '/admin/payments': 'payment_management',
      '/admin/settings': 'settings',
    };
    return moduleMap[path] || null;
  };

  // Check if user has access to a module
  const hasModuleAccess = (path: string): boolean => {
    // If user is the original super admin or has no admin_permissions, show all modules
    if (!user?.admin_permissions || user.admin_permissions.length === 0) {
      return true;
    }

    // Get the module name for this path
    const moduleName = getModuleForPath(path);
    if (!moduleName) return true;

    // Check if user has view permission for this module
    const permission = user.admin_permissions.find(p => p.module === moduleName);
    return permission?.can_view || false;
  };

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: BarChart3 },
    { path: '/admin/support-tickets', label: 'Support Tickets', icon: MessageCircle },
    { path: '/admin/shops', label: 'Shops Management', icon: Store },
    { path: '/admin/payments', label: 'Payment Management', icon: Wallet },
    { path: '/admin/settings', label: 'Settings', icon: SettingsIcon },
  ].filter(item => hasModuleAccess(item.path)); // Filter based on permissions

  const handleLogout = async () => {
    try {
      await logout();
      customToast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      customToast.error('Error logging out');
    }
  };

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div
      className="mobile-layout-container flex transition-all duration-300 h-screen w-screen overflow-hidden"
      style={{
        backgroundColor: theme === 'dark' ? '#0f172a' : '#f8fafc'
      }}
    >
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } w-72 lg:w-auto`}>
        <div className={`flex flex-col h-full transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'
          } w-72 ${theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-slate-200'
          } border-r`}>
          {/* Logo */}
          <div className={`flex h-20 items-center justify-between border-b border-inherit transition-all duration-300 ease-in-out px-6 ${sidebarCollapsed ? 'lg:px-2' : ''
            }`}>
            <div className={`flex items-center space-x-3 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl overflow-hidden ${theme === 'dark'
                ? 'bg-gradient-to-br from-blue-600 to-indigo-600'
                : 'bg-gradient-to-br from-blue-600 to-indigo-600'
                }`}>
                {getSetting('site_logo') && !logoImageFailed ? (
                  <img
                    src={`/storage/${getSetting('site_logo')}`}
                    alt="Site Logo"
                    className="w-full h-full object-cover"
                    onLoad={() => {
                      console.log('✅ Admin sidebar logo loaded successfully:', getSetting('site_logo'));
                    }}
                    onError={() => {
                      const logoUrl = `/storage/${getSetting('site_logo')}`;
                      console.error('❌ Admin sidebar logo failed to load');
                      console.error('   URL:', logoUrl);
                      console.error('   Setting value:', getSetting('site_logo'));
                      console.error('   API Base URL:', import.meta.env.VITE_API_BASE_URL);
                      setLogoImageFailed(true);
                    }}
                  />
                ) : (
                  <>
                    {logoImageFailed && console.log('📦 Using fallback Package icon for admin sidebar logo')}
                    <Package className="h-6 w-6 text-white" />
                  </>
                )}
              </div>
              <div className="transition-opacity duration-300 ease-in-out">
                <h1 className={`text-heading text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                  }`}>
                  Super Admin
                </h1>
                <p className={`text-caption ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                  {getSetting('site_name') || 'NextGen - Shop'}
                </p>
              </div>
            </div>
            <div className={`hidden lg:flex mx-auto h-10 w-10 items-center justify-center rounded-xl overflow-hidden ${sidebarCollapsed ? '' : 'lg:hidden'
              } ${theme === 'dark'
                ? 'bg-gradient-to-br from-blue-600 to-indigo-600'
                : 'bg-gradient-to-br from-blue-600 to-indigo-600'
              }`}>
              {getSetting('site_logo') && !logoImageFailed ? (
                <img
                  src={`/storage/${getSetting('site_logo')}`}
                  alt="Site Logo"
                  className="w-full h-full object-cover"
                  onError={() => {
                    const logoUrl = `/storage/${getSetting('site_logo')}`;
                    console.error('❌ Admin sidebar collapsed logo failed to load');
                    console.error('   URL:', logoUrl);
                    console.error('   Setting value:', getSetting('site_logo'));
                    console.error('   API Base URL:', import.meta.env.VITE_API_BASE_URL);
                    setLogoImageFailed(true);
                  }}
                />
              ) : (
                <Package className="h-6 w-6 text-white" />
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${theme === 'dark'
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto py-6 space-y-2 transition-all duration-300 ease-in-out px-4 ${sidebarCollapsed ? 'lg:px-3' : ''}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    console.log('🔗 Clicked menu item:', item.path);
                    console.log('📍 Current location:', location.pathname);
                    setSidebarOpen(false);
                  }}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={`group flex items-center space-x-3 px-4 py-3 ${sidebarCollapsed ? 'lg:justify-center lg:px-3 lg:py-3 lg:space-x-0' : ''
                    } rounded-xl text-sm font-medium transition-all duration-200 ${active
                      ? theme === 'dark'
                        ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50'
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                      : theme === 'dark'
                        ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                >
                  <Icon className={`flex-shrink-0 h-5 w-5 transition-all duration-200 ${sidebarCollapsed ? 'lg:h-6 lg:w-6' : ''
                    } ${active
                      ? theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      : theme === 'dark' ? 'text-slate-400 group-hover:text-slate-300' : 'text-slate-400 group-hover:text-slate-600'
                    }`} />
                  <span className={`transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:hidden lg:w-0 lg:opacity-0' : 'lg:opacity-100'}`}>
                    {item.label}
                  </span>
                  {active && (
                    <div className={`ml-auto h-2 w-2 rounded-full transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:hidden lg:opacity-0' : 'lg:opacity-100'
                      } ${theme === 'dark' ? 'bg-blue-400' : 'bg-blue-600'
                      }`} />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Collapse/Expand Button - Desktop Only */}
          <div className={`hidden lg:flex border-t border-inherit transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'justify-center px-2' : 'justify-end px-6'} py-4`}>
            <button
              onClick={toggleSidebarCollapse}
              className={`p-2 rounded-lg transition-all duration-300 ease-in-out ${theme === 'dark'
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight className="h-5 w-5 transition-transform duration-300" /> : <ChevronLeft className="h-5 w-5 transition-transform duration-300" />}
            </button>
          </div>

          {/* User section - always at bottom */}
          <div className="flex-shrink-0 border-t border-inherit p-4 relative">
            <div className="relative">
              {/* Collapsed view - Desktop only */}
              <button
                onClick={() => setSidebarProfileDropdownOpen(!sidebarProfileDropdownOpen)}
                className={`hidden lg:flex w-full items-center justify-center rounded-xl transition-all duration-200 ${sidebarCollapsed ? '' : 'lg:hidden'
                  } ${sidebarProfileDropdownOpen
                    ? theme === 'dark'
                      ? 'ring-2 ring-blue-500/50'
                      : 'ring-2 ring-blue-500/20'
                    : ''
                  }`}
                title={user?.full_name || 'Profile'}
              >
                <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/10">
                  {getProfilePictureUrl(user) ? (
                    <img
                      src={getProfilePictureUrl(user)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                  <div
                    className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center"
                    style={{ display: getProfilePictureUrl(user) ? 'none' : 'flex' }}
                  >
                    <span className="text-sm font-medium text-white">
                      {user?.full_name?.charAt(0).toUpperCase() || 'S'}
                    </span>
                  </div>
                </div>
              </button>

              {/* Expanded view */}
              <button
                onClick={() => setSidebarProfileDropdownOpen(!sidebarProfileDropdownOpen)}
                className={`flex w-full items-center space-x-3 p-3 rounded-xl transition-all duration-200 ${sidebarCollapsed ? 'lg:hidden' : ''
                  } ${sidebarProfileDropdownOpen
                    ? theme === 'dark'
                      ? 'bg-slate-700 ring-2 ring-blue-500/50'
                      : 'bg-slate-100 ring-2 ring-blue-500/20'
                    : theme === 'dark'
                      ? 'bg-slate-800/50 hover:bg-slate-700'
                      : 'bg-white hover:bg-slate-50'
                  } border ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'
                  } shadow-sm`}
              >
                <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/10">
                  {getProfilePictureUrl(user) ? (
                    <img
                      src={getProfilePictureUrl(user)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                  <div
                    className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center"
                    style={{ display: getProfilePictureUrl(user) ? 'none' : 'flex' }}
                  >
                    <span className="text-sm font-medium text-white">
                      {user?.full_name?.charAt(0).toUpperCase() || 'S'}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                      }`} title={user?.full_name || 'Super Admin'}>
                      {truncateText(user?.full_name || 'Super Admin', 15)}
                    </p>
                    <SubscriptionBadge
                      subscriptionStatus={user?.shop?.subscription_status}
                      planType={user?.shop?.plan_type}
                      trialEndsAt={user?.shop?.trial_ends_at}
                      theme={theme}
                      size="sm"
                    />
                  </div>
                  <p className={`text-xs truncate ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                    Super Administrator
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${sidebarProfileDropdownOpen ? 'rotate-180' : 'rotate-0'
                  } ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
              </button>

              {/* Dropdown Menu */}
              {sidebarProfileDropdownOpen && (
                <div
                  id="sidebar-profile-dropdown"
                  className={`absolute bottom-full left-0 right-0 mb-2 py-2 rounded-xl shadow-xl border backdrop-blur-sm z-50 ${theme === 'dark'
                    ? 'bg-slate-800/95 border-slate-700'
                    : 'bg-white/95 border-slate-200'
                    }`}
                >
                  <button
                    onClick={() => {
                      setShowProfileModal(true);
                      setSidebarProfileDropdownOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-colors ${theme === 'dark'
                      ? 'hover:bg-slate-700 text-slate-200'
                      : 'hover:bg-slate-50 text-slate-700'
                      }`}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="text-sm">Edit Profile</span>
                  </button>

                  <div className={`my-1 h-px ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'
                    }`} />

                  <button
                    onClick={() => {
                      handleLogout();
                      setSidebarProfileDropdownOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-colors ${theme === 'dark'
                      ? 'hover:bg-red-900/20 text-red-400'
                      : 'hover:bg-red-50 text-red-600'
                      }`}
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm">Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 grid grid-rows-[auto_1fr_auto] min-w-0 min-h-0 mobile-main-content overflow-hidden">
        {/* Top bar */}
        <header className={`flex h-16 sm:h-20 items-center transition-colors duration-200 ${theme === 'dark'
          ? 'bg-slate-800/80 border-slate-700'
          : 'bg-white/80 border-slate-200'
          } border-b backdrop-blur-xl`}>
          <div className="w-full px-4 sm:px-6">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className={`lg:hidden flex-shrink-0 p-2 rounded-lg transition-colors ${theme === 'dark'
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                >
                  <Menu className="h-5 w-5" />
                </button>

                {/* Page Title */}
                <div className="hidden sm:block">
                  <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                    }`}>
                    Super Admin Dashboard
                  </h2>
                  <p className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                    }`}>
                    Manage shops and system settings
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 flex-shrink-0">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className={`p-2 rounded-xl transition-colors ${theme === 'dark'
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                  title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>

                {/* Profile Dropdown */}
                <div className="relative" id="profile-dropdown">
                  <button
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-colors ${theme === 'dark'
                      ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center overflow-hidden ${theme === 'dark'
                      ? 'bg-gradient-to-br from-slate-600 to-slate-700'
                      : 'bg-gradient-to-br from-slate-600 to-slate-700'
                      }`}>
                      {getProfilePictureUrl(user) ? (
                        <img
                          src={getProfilePictureUrl(user)}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      <span
                        className="text-xs font-medium text-white"
                        style={{ display: getProfilePictureUrl(user) ? 'none' : 'block' }}
                      >
                        {user?.full_name?.charAt(0).toUpperCase() || 'S'}
                      </span>
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                          }`} title={user?.full_name || 'Super Admin'}>
                          {truncateText(user?.full_name || 'Super Admin', 20)}
                        </p>
                        <SubscriptionBadge
                          subscriptionStatus={user?.shop?.subscription_status}
                          planType={user?.shop?.plan_type}
                          trialEndsAt={user?.shop?.trial_ends_at}
                          theme={theme}
                          size="sm"
                        />
                      </div>
                      <p className={`text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`}>
                        Super Administrator
                      </p>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${profileDropdownOpen ? 'rotate-180' : ''
                      }`} />
                  </button>

                  {/* Dropdown Menu */}
                  {profileDropdownOpen && (
                    <div className={`absolute right-0 mt-2 w-48 rounded-xl shadow-lg border z-50 ${theme === 'dark'
                      ? 'bg-slate-800 border-slate-700'
                      : 'bg-white border-slate-200'
                      }`}>
                      <div className="p-1">
                        <button
                          onClick={() => {
                            setShowProfileModal(true);
                            setProfileDropdownOpen(false);
                          }}
                          className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${theme === 'dark'
                            ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                            }`}
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit Profile</span>
                        </button>

                        <div className={`my-1 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'
                          }`} />

                        <button
                          onClick={() => {
                            handleLogout();
                            setProfileDropdownOpen(false);
                          }}
                          className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${theme === 'dark'
                            ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                            : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                            }`}
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="overflow-y-auto mobile-scroll-content">
          <div className="p-4 sm:p-6">
            <div className="max-w-7xl mx-auto animate-fade-in">
              {children}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className={`py-3 px-4 sm:py-4 sm:px-6 border-t ${theme === 'dark' ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'
          }`}>
          <div className="flex flex-col space-y-2 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
            <p className={`text-xs flex items-center justify-center sm:justify-start ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
              }`}>
              Built with{' '}
              <Heart className="animate-heartbeat mx-1 h-3 w-3 text-red-500 fill-current" />
              {' '}by{' '}
              <a
                href="https://www.hindukushsoft.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline transition-colors text-blue-600 hover:text-blue-800 ml-1"
              >
                HindukushSoft Technologies
              </a>
            </p>
            <p className={`text-xs text-center sm:text-right ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
              }`}>
              © {new Date().getFullYear()} {getSetting('site_name') || 'NextGen - Shop'}. All rights reserved.
            </p>
          </div>
        </footer>
      </div>

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
